/**
 * Select com busca (combobox) para campos de referência (FK) — profissão, cargo,
 * administradora. Busca as opções de um endpoint, mostra o rótulo humano (nunca o
 * UUID) e filtra por digitação, o que importa quando a lista é grande (as ~200
 * profissões CBO não cabem num dropdown chapado).
 *
 * Emite o `id` selecionado por `onChange`; string vazia quando limpo. Sem dependência
 * de combobox pronto — é um input + lista filtrada, com teclado (setas, Enter, Esc).
 *
 * Com `permitirCriar`, quando o texto digitado não casa com nenhuma opção existente,
 * aparece um "＋ Criar «texto»" que faz `POST endpoint` com `{[chaveRotulo]: texto}`,
 * adiciona a opção nova e já a seleciona — para catálogos que nascem vazios (cargos) ou
 * onde o operador precisa de um valor que ainda não existe. O `POST` usa o mesmo endpoint.
 */
import { Check, ChevronsUpDown, Plus, X } from 'lucide-react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { toast } from 'sonner';
import { api } from '@/lib/api';
import { cn } from '@/lib/utils';

type Opcao = { valor: string; rotulo: string };

export function ComboReferencia({
  id,
  valor,
  onChange,
  endpoint,
  chaveRotulo = 'nome',
  obrigatorio,
  permitirCriar,
}: {
  id: string;
  valor: string;
  onChange: (id: string) => void;
  endpoint: string;
  chaveRotulo?: string;
  obrigatorio?: boolean;
  permitirCriar?: boolean;
}) {
  const [opcoes, setOpcoes] = useState<Opcao[]>([]);
  const [aberto, setAberto] = useState(false);
  const [filtro, setFiltro] = useState('');
  const [destaque, setDestaque] = useState(0);
  const [criando, setCriando] = useState(false);
  // Rótulo do item SELECIONADO — resolvido por id quando ele não está na página carregada (ex.: a
  // administradora vinculada não está entre as 50 primeiras empresas). Sem isso o campo fica vazio.
  const [rotuloSelecionado, setRotuloSelecionado] = useState('');
  const raiz = useRef<HTMLDivElement>(null);
  const debounce = useRef<ReturnType<typeof setTimeout>>(undefined);

  // Carrega opções do endpoint. Aceita resposta em array (profissões/cargos/tags) OU paginada
  // (`{itens,total}`, como /cadastro/empresas) e busca no servidor (`busca`) — as listas grandes
  // não cabem num dropdown; endpoints de array simplesmente ignoram o parâmetro e devolvem tudo.
  const carregar = useCallback(
    async (termo: string) => {
      const sep = endpoint.includes('?') ? '&' : '?';
      const url = `${endpoint}${sep}busca=${encodeURIComponent(termo)}&limite=50`;
      const r = await api.get<unknown>(url);
      if (!r.ok) return;
      const bruto = r.dados as { itens?: Record<string, unknown>[] } | Record<string, unknown>[];
      const itens = Array.isArray(bruto) ? bruto : (bruto.itens ?? []);
      setOpcoes(
        itens.map((o) => ({ valor: String(o.id), rotulo: String(o[chaveRotulo] ?? o.id) })),
      );
    },
    [endpoint, chaveRotulo],
  );

  useEffect(() => {
    void carregar('');
  }, [carregar]);

  // Resolve o rótulo do selecionado: se já está nas opções, usa; senão busca por id no endpoint.
  useEffect(() => {
    if (!valor) {
      setRotuloSelecionado('');
      return;
    }
    const naLista = opcoes.find((o) => o.valor === valor);
    if (naLista) {
      setRotuloSelecionado(naLista.rotulo);
      return;
    }
    let vivo = true;
    void api.get<Record<string, unknown>>(`${endpoint}/${valor}`).then((r) => {
      if (vivo && r.ok) setRotuloSelecionado(String(r.dados[chaveRotulo] ?? r.dados.id ?? ''));
    });
    return () => {
      vivo = false;
    };
  }, [valor, opcoes, endpoint, chaveRotulo]);

  // Fecha ao clicar fora.
  useEffect(() => {
    if (!aberto) return;
    function fora(e: MouseEvent) {
      if (raiz.current && !raiz.current.contains(e.target as Node)) setAberto(false);
    }
    document.addEventListener('mousedown', fora);
    return () => document.removeEventListener('mousedown', fora);
  }, [aberto]);

  const filtradas = useMemo(() => {
    const f = filtro.trim().toLowerCase();
    return f ? opcoes.filter((o) => o.rotulo.toLowerCase().includes(f)) : opcoes;
  }, [opcoes, filtro]);

  // Digitar refaz a busca no servidor (debounced) — para listas grandes (empresas) o filtro não
  // pode ser só client-side sobre as 50 já carregadas.
  function digitar(texto: string) {
    setFiltro(texto);
    setAberto(true);
    setDestaque(0);
    clearTimeout(debounce.current);
    debounce.current = setTimeout(() => void carregar(texto), 250);
  }

  const novo = filtro.trim();
  const podeCriar =
    !!permitirCriar &&
    !criando &&
    novo.length > 0 &&
    !opcoes.some((o) => o.rotulo.toLowerCase() === novo.toLowerCase());

  function escolher(o: Opcao) {
    onChange(o.valor);
    setFiltro('');
    setAberto(false);
  }

  async function criar() {
    if (!podeCriar) return;
    setCriando(true);
    const r = await api.post<Record<string, unknown>>(endpoint, { [chaveRotulo]: novo });
    setCriando(false);
    if (!r.ok) return void toast.error(r.erro);
    const nova = { valor: String(r.dados.id), rotulo: String(r.dados[chaveRotulo] ?? novo) };
    setOpcoes((prev) => [...prev, nova].sort((a, b) => a.rotulo.localeCompare(b.rotulo, 'pt-BR')));
    escolher(nova);
  }

  function teclado(e: React.KeyboardEvent) {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setAberto(true);
      setDestaque((d) => Math.min(d + 1, filtradas.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setDestaque((d) => Math.max(d - 1, 0));
    } else if (e.key === 'Enter') {
      e.preventDefault();
      const o = filtradas[destaque];
      // Sem opção destacada mas com texto novo: Enter cria (quando permitido).
      if (o) escolher(o);
      else if (podeCriar) void criar();
    } else if (e.key === 'Escape') {
      setAberto(false);
    }
  }

  return (
    <div ref={raiz} className="relative">
      <div
        className={cn(
          'flex h-10 items-center rounded-md border border-border bg-input pr-2',
          'focus-within:border-border-strong focus-within:shadow-[var(--focus-ring)]',
        )}
      >
        <input
          id={id}
          role="combobox"
          aria-expanded={aberto}
          aria-controls={`${id}-lista`}
          autoComplete="off"
          required={obrigatorio && !valor}
          placeholder={rotuloSelecionado ? undefined : 'Buscar…'}
          value={aberto ? filtro : rotuloSelecionado}
          onFocus={() => {
            setAberto(true);
            setDestaque(0);
          }}
          onChange={(e) => digitar(e.target.value)}
          onKeyDown={teclado}
          className="h-full flex-1 bg-transparent px-3 text-[15px] text-text outline-none placeholder:text-text-tertiary"
        />
        {valor && (
          <button
            type="button"
            aria-label="Limpar"
            onClick={() => onChange('')}
            className="rounded-sm p-1 text-text-tertiary hover:text-text"
          >
            <X className="size-4" />
          </button>
        )}
        <ChevronsUpDown className="size-4 shrink-0 text-text-tertiary" />
      </div>

      {aberto && (
        <ul
          id={`${id}-lista`}
          role="listbox"
          className="absolute z-[var(--z-dropdown)] mt-1 max-h-64 w-full overflow-y-auto rounded-lg border border-border bg-surface p-1 shadow-[var(--shadow-md)]"
        >
          {filtradas.length === 0 && !podeCriar && (
            <li className="px-3 py-2 text-[13px] text-text-tertiary">Nada encontrado.</li>
          )}
          {filtradas.slice(0, 100).map((o, i) => (
            <li key={o.valor}>
              <button
                type="button"
                role="option"
                aria-selected={o.valor === valor}
                onMouseEnter={() => setDestaque(i)}
                onClick={() => escolher(o)}
                className={cn(
                  'flex w-full items-center gap-2 rounded-sm px-3 py-2 text-left text-[15px] outline-none',
                  i === destaque ? 'bg-surface-hover' : '',
                )}
              >
                <Check
                  className={cn('size-4 shrink-0', o.valor === valor ? 'text-accent' : 'opacity-0')}
                />
                {o.rotulo}
              </button>
            </li>
          ))}
          {podeCriar && (
            <li>
              <button
                type="button"
                onClick={() => void criar()}
                className="flex w-full items-center gap-2 rounded-sm px-3 py-2 text-left text-[15px] text-accent outline-none hover:bg-surface-hover"
              >
                <Plus className="size-4 shrink-0" />
                Criar “{novo}”
              </button>
            </li>
          )}
        </ul>
      )}
    </div>
  );
}
